import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import RelativeDateFields from '../../../RelativeDateFields';
import decodeRelativeDates from '@afosto/utils-react/date/decodeRelativeDates';
import encodeRelativeDates from '@afosto/utils-react/date/encodeRelativeDates';
import getTimeZone from '@afosto/utils/date/getTimeZone';
import translations from './translations';

const RelativeDateFilter = ({ onChange, operator, settings, suggestions, value, ...props }) => {
  const { relativeNow = false, showTime = true } = settings ?? {};
  const defaultDate = {
    quantity: '',
    key: 'days',
    direction: 'ahead',
    at: {
      timezone: getTimeZone(),
      hour: relativeNow && showTime ? 'now' : 0,
      minute: relativeNow && showTime ? 'now' : 0,
      day_of_week: '',
      day_of_month: '',
    },
  };
  const defaultDateRef = useRef(defaultDate);
  const [relativeDates, setRelativeDates] = useState([defaultDate, defaultDate]);

  useEffect(() => {
    defaultDateRef.current = defaultDate;
  });

  useEffect(() => {
    const decodeDateValues = async () => {
      try {
        const decodedValues = await decodeRelativeDates(value);
        const filters = decodedValues.map(decodedValue => decodedValue?.query);

        setRelativeDates(filters);
      } catch (err) {
        setRelativeDates([]);
      }
    };

    if (operator && value && value?.length > 0) {
      decodeDateValues().catch(() => {
        // Catch only
      });
    } else {
      setRelativeDates([defaultDateRef?.current, defaultDateRef?.current]);
    }
  }, [operator, value]);

  const handleRelativeChanges = (relativeDate, name, changedValue) => {
    let newValue;

    if (name === 'time') {
      if (changedValue) {
        newValue = {
          ...(relativeDate ?? {}),
          at: {
            ...(relativeDate?.at ?? {}),
            hour: changedValue.getHours().toString(),
            minute: changedValue.getMinutes().toString(),
          },
        };
      } else {
        newValue = {
          ...(relativeDate ?? {}),
          at: {
            ...(relativeDate?.at ?? {}),
            hour: '',
            minute: '',
          },
        };
      }
    }

    const atFields = ['day_of_week', 'day_of_month'];

    if (atFields.includes(name)) {
      newValue = {
        ...(relativeDate ?? {}),
        at: {
          ...(relativeDate?.at ?? {}),
          [name]: changedValue.toString(),
        },
      };

      if (name === 'day_of_week') {
        newValue.at.day_of_month = '';
      } else {
        newValue.at.day_of_week = '';
      }
    }

    if (name !== 'time' && !atFields.includes(name)) {
      newValue = { ...(relativeDate ?? {}), [name]: changedValue };
    }

    return newValue;
  };

  const formatRelativeDate = relativeDate => ({
    ...relativeDate,
    direction: relativeDate?.quantity < 0 ? 'ago' : 'ahead',
    quantity: Math.abs(relativeDate?.quantity ?? 0),
    at: relativeNow
      ? {
          ...(relativeDate?.at ?? {}),
          hour: 'now',
          minute: 'now',
          day_of_week: '',
          day_of_month: '',
        }
      : relativeDate?.at,
  });

  const handleChange = name => async (ev, changedValue) => {
    try {
      const [relativeDate] = relativeDates;
      const relativeDateChanges = handleRelativeChanges(relativeDate, name, changedValue);
      setRelativeDates([relativeDateChanges]);

      const formattedRelativeDate = formatRelativeDate(relativeDateChanges);

      if (onChange && typeof onChange === 'function') {
        const dates = await encodeRelativeDates([formattedRelativeDate]);
        const filters = dates.map(date => date?.filter);
        onChange({}, filters);
      }
    } catch (err) {
      if (onChange && typeof onChange === 'function') {
        onChange({}, []);
      }
    }
  };

  const handleChangeBetween = idx => name => async (ev, changedValue) => {
    try {
      const relativeDate = relativeDates[idx];
      const relativeDateChanges = handleRelativeChanges(relativeDate, name, changedValue);

      if (idx === 0) {
        setRelativeDates([relativeDateChanges, relativeDates[1]]);
      } else {
        setRelativeDates([relativeDates[0], relativeDateChanges]);
      }

      const formattedRelativeDate = formatRelativeDate(relativeDateChanges);

      if (onChange && typeof onChange === 'function') {
        let encodeDates;

        if (idx === 0) {
          encodeDates = [formattedRelativeDate, relativeDates[1]];
        } else {
          encodeDates = [relativeDates[0], formattedRelativeDate];
        }

        const dates = await encodeRelativeDates(encodeDates);
        const filters = dates.map(date => date?.filter);
        onChange({}, filters);
      }
    } catch (err) {
      if (onChange && typeof onChange === 'function') {
        onChange({}, []);
      }
    }
  };

  const [relativeDate] = relativeDates;

  if (operator === 'between') {
    return (
      <>
        <Box my={1}>
          <Typography variant="body2">
            <FormattedMessage {...translations.from} />
          </Typography>
          <RelativeDateFields
            {...props}
            relativeDate={relativeDates[0]}
            onChange={handleChangeBetween(0)}
            showAdvanced={!relativeNow}
            showTime={showTime}
          />
        </Box>
        <Box>
          <Typography variant="body2">
            <FormattedMessage {...translations.to} />
          </Typography>
          <RelativeDateFields
            {...props}
            relativeDate={relativeDates[1]}
            onChange={handleChangeBetween(1)}
            showAdvanced={!relativeNow}
            showTime={showTime}
          />
        </Box>
      </>
    );
  }

  return (
    <RelativeDateFields
      {...props}
      relativeDate={relativeDate}
      onChange={handleChange}
      showAdvanced={!relativeNow}
      showTime={showTime}
    />
  );
};

RelativeDateFilter.propTypes = {
  onChange: PropTypes.func,
  operator: PropTypes.string,
  settings: PropTypes.object,
  suggestions: PropTypes.array,
  value: PropTypes.array,
};

RelativeDateFilter.defaultProps = {
  onChange: undefined,
  operator: undefined,
  settings: {},
  suggestions: [],
  value: [],
};

export default RelativeDateFilter;
